import { PERMISSIONS } from '../config/permissions';

/**
 * Partners Permission Utilities
 * Provides consistent permission checking for all Partners operations
 */

/**
 * Check if user can view partners
 * @param {Object} userPermissions - User permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {boolean}
 */
export const canViewPartners = (userPermissions, isMainVendor) => {
  if (isMainVendor) return true;
  
  return hasPermission(
    userPermissions, 
    PERMISSIONS.MODULES.PARTNER_MANAGEMENT, 
    PERMISSIONS.ACTIONS.VIEW_PARTNERS
  );
};

/**
 * Check if user can create partners
 * @param {Object} userPermissions - User permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {boolean}
 */
export const canCreatePartners = (userPermissions, isMainVendor) => {
  if (isMainVendor) return true;
  
  return hasPermission(
    userPermissions, 
    PERMISSIONS.MODULES.PARTNER_MANAGEMENT, 
    PERMISSIONS.ACTIONS.CREATE_PARTNER
  );
};

/**
 * Check if user can edit partners
 * @param {Object} userPermissions - User permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {boolean}
 */
export const canEditPartners = (userPermissions, isMainVendor) => {
  if (isMainVendor) return true;
  
  return hasPermission(
    userPermissions, 
    PERMISSIONS.MODULES.PARTNER_MANAGEMENT, 
    PERMISSIONS.ACTIONS.EDIT_PARTNER
  );
};

/**
 * Check if user can delete partners
 * @param {Object} userPermissions - User permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {boolean}
 */
export const canDeletePartners = (userPermissions, isMainVendor) => {
  if (isMainVendor) return true;
  
  return hasPermission(
    userPermissions, 
    PERMISSIONS.MODULES.PARTNER_MANAGEMENT, 
    PERMISSIONS.ACTIONS.DELETE_PARTNER
  );
};

/**
 * Check if user has any partner management permissions
 * @param {Object} userPermissions - User permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {boolean}
 */
export const hasAnyPartnerPermission = (userPermissions, isMainVendor) => {
  if (isMainVendor) return true;
  
  return canViewPartners(userPermissions, false) ||
         canCreatePartners(userPermissions, false) ||
         canEditPartners(userPermissions, false) ||
         canDeletePartners(userPermissions, false);
};

/**
 * Get all partner permissions for a user
 * @param {Object} userPermissions - User permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {Object}
 */
export const getPartnerPermissions = (userPermissions, isMainVendor) => {
  return {
    canView: canViewPartners(userPermissions, isMainVendor),
    canCreate: canCreatePartners(userPermissions, isMainVendor),
    canEdit: canEditPartners(userPermissions, isMainVendor),
    canDelete: canDeletePartners(userPermissions, isMainVendor),
    hasAny: hasAnyPartnerPermission(userPermissions, isMainVendor)
  };
};

/**
 * Validate partner operation permission
 * @param {string} operation - Operation to validate (view, create, edit, delete)
 * @param {Object} userPermissions - User permissions object
 * @param {boolean} isMainVendor - Whether user is main vendor
 * @returns {Object} - { hasPermission: boolean, message: string }
 */
export const validatePartnerOperation = (operation, userPermissions, isMainVendor) => {
  const permissionMap = {
    view: canViewPartners,
    create: canCreatePartners,
    edit: canEditPartners,
    delete: canDeletePartners
  };

  const hasPermission = permissionMap[operation]?.(userPermissions, isMainVendor) || false;
  
  const messages = {
    view: "You don't have permission to view partners",
    create: "You don't have permission to create partners",
    edit: "You don't have permission to edit partners",
    delete: "You don't have permission to delete partners"
  };

  return {
    hasPermission,
    message: hasPermission ? null : messages[operation] || "Operation not allowed"
  };
};

/**
 * Helper function to check if user has a specific permission
 * @param {Object} userPermissions - User permissions object
 * @param {string} module - Permission module
 * @param {string} action - Permission action
 * @returns {boolean}
 */
const hasPermission = (userPermissions, module, action) => {
  if (!userPermissions || !userPermissions[module]) {
    return false;
  }
  
  return userPermissions[module].includes(action);
};

/**
 * Get required permissions for different partner operations
 * @returns {Object}
 */
export const PARTNER_PERMISSIONS = {
  VIEW: {
    module: PERMISSIONS.MODULES.PARTNER_MANAGEMENT,
    action: PERMISSIONS.ACTIONS.VIEW_PARTNERS
  },
  CREATE: {
    module: PERMISSIONS.MODULES.PARTNER_MANAGEMENT,
    action: PERMISSIONS.ACTIONS.CREATE_PARTNER
  },
  EDIT: {
    module: PERMISSIONS.MODULES.PARTNER_MANAGEMENT,
    action: PERMISSIONS.ACTIONS.EDIT_PARTNER
  },
  DELETE: {
    module: PERMISSIONS.MODULES.PARTNER_MANAGEMENT,
    action: PERMISSIONS.ACTIONS.DELETE_PARTNER
  }
}; 